import { useRef, useState } from "react"
import { PostType } from "../../../../types/scheduled-post"
import { MediaItem } from "../../../../hooks/useFileUpload"

const SPEC_INFO: Record<PostType, { formats: string; imageSize?: string; videoSize?: string; duration?: string; note?: string }> = {
  feed:     { formats: "JPEG / PNG", imageSize: "最大 8MB", note: "動画不可" },
  reel:     { formats: "MP4 / MOV", videoSize: "最大 100MB", duration: "3秒〜90秒", note: "推奨 9:16・画像不可" },
  story:    { formats: "JPEG / PNG・MP4 / MOV", imageSize: "最大 8MB", videoSize: "最大 100MB", duration: "3秒〜60秒", note: "推奨 9:16" },
  carousel: { formats: "JPEG / PNG・MP4 / MOV", imageSize: "最大 8MB", videoSize: "最大 100MB", duration: "3秒〜60秒", note: "2〜10枚・合計 400MB以内" },
}

type Props = {
  postType: PostType
  items: MediaItem[]
  isDragging: boolean
  isValidatingAny: boolean
  hasFileErrors: boolean
  tooManyFiles: boolean
  carouselSizeError?: string | null
  onFilesAdded: (files: File[]) => void
  onRemoveFile: (index: number) => void
  onReorder: (from: number, to: number) => void
  onDrop: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDragLeave: () => void
}

const MediaUploader = ({
  postType, items, isDragging, isValidatingAny, hasFileErrors, tooManyFiles, carouselSizeError,
  onFilesAdded, onRemoveFile, onReorder, onDrop, onDragOver, onDragLeave,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [playUrl, setPlayUrl] = useState<string | null>(null)
  const [showSpec, setShowSpec] = useState(false)
  const dragIndexRef = useRef<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const spec = SPEC_INFO[postType]

  const getVideoUrl = (item: MediaItem): string | null => {
    if (item.kind === "existing" && item.content_type.startsWith("video/")) return item.url
    if (item.kind === "new" && item.file.type.startsWith("video/")) return item.previewUrl
    return null
  }

  const getImageUrl = (item: MediaItem): string | null => {
    if (item.kind === "existing" && !item.content_type.startsWith("video/")) return item.url
    if (item.kind === "new" && item.file.type.startsWith("image/")) return item.previewUrl
    return null
  }

  const getErrors = (item: MediaItem): string[] => {
    if (item.kind !== "new") return []
    return item.uploadError ? [...item.errors, item.uploadError] : item.errors
  }

  const getWarnings = (item: MediaItem): string[] => {
    if (item.kind !== "new") return []
    return item.warnings
  }

  const isProcessing = (item: MediaItem): boolean =>
    item.kind === "new" && (item.validating || item.uploading)

  // アイテムD&D（メディア並び替え用）
  const handleItemDragStart = (e: React.DragEvent, index: number) => {
    e.stopPropagation()
    dragIndexRef.current = index
    e.dataTransfer.effectAllowed = "move"
  }

  const handleItemDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = "move"
    setDragOverIndex(index)
  }

  const handleItemDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault()
    e.stopPropagation()
    const fromIndex = dragIndexRef.current
    if (fromIndex !== null && fromIndex !== toIndex) {
      onReorder(fromIndex, toIndex)
    }
    dragIndexRef.current = null
    setDragOverIndex(null)
  }

  const handleItemDragEnd = () => {
    dragIndexRef.current = null
    setDragOverIndex(null)
  }

  const allErrors = items.flatMap((item, fi) =>
    getErrors(item).map((err, ei) => ({ key: `${fi}-${ei}`, err }))
  )

  const allWarnings = items.flatMap((item, fi) =>
    getWarnings(item).map((warn, wi) => ({ key: `${fi}-${wi}`, warn }))
  )

  return (
    <section className="post-create-form__section">
      <div className="media-uploader__header">
        <h2 className="post-create-form__section-title">メディア</h2>
        <div className="media-spec-trigger" onClick={() => setShowSpec(v => !v)}>
          <i className="bi bi-info-circle" />
          {showSpec && (
            <div className="media-spec-popover" onClick={e => e.stopPropagation()}>
              <div className="media-spec-popover__title">アップロード仕様（{postType}）</div>
              <table className="media-spec-popover__table">
                <tbody>
                  <tr><th>フォーマット</th><td>{spec.formats}</td></tr>
                  {spec.imageSize && <tr><th>画像サイズ</th><td>{spec.imageSize}</td></tr>}
                  {spec.videoSize && <tr><th>動画サイズ</th><td>{spec.videoSize}</td></tr>}
                  {spec.duration  && <tr><th>動画の長さ</th><td>{spec.duration}</td></tr>}
                  {spec.note      && <tr><th>備考</th><td>{spec.note}</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {postType === "carousel" && (
        <p className="form-group__hint" style={{ marginBottom: 12 }}>
          カルーセル投稿の場合は<strong>2〜10枚</strong>アップロードしてください
        </p>
      )}

      <div
        className={`dropzone ${isDragging ? "dropzone--active" : ""}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div className="dropzone__icon"><i className="bi bi-cloud-upload" /></div>
        <div className="dropzone__text">クリックまたはドラッグ&ドロップでファイルを選択</div>
        <div className="dropzone__hint">
          {postType === "reel" ? "MP4 / MOV（動画）対応" : postType === "feed" ? "JPEG/PNG（画像）対応" : "JPEG/PNG（画像）・MP4 / MOV（動画）対応"}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={postType === "reel" ? "video/mp4,video/quicktime" : postType === "feed" ? "image/jpeg,image/png" : "image/jpeg,image/png,video/mp4,video/quicktime"}
          multiple={postType === "carousel"}
          style={{ display: "none" }}
          onChange={e => {
            if (e.target.files) {
              onFilesAdded(Array.from(e.target.files))
              e.target.value = ""
            }
          }}
        />
      </div>

      {(isValidatingAny || items.some(i => i.kind === "new" && i.uploading)) && (
        <p style={{ marginTop: 8, fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 6 }}>
          <span className="spinner" style={{ borderColor: "#d1d5db", borderTopColor: "#6366f1" }} />
          {isValidatingAny ? "検証中..." : "アップロード中..."}
        </p>
      )}

      {items.length > 0 && (
        <div className="media-previews">
          {items.map((item, i) => {
            const videoUrl  = getVideoUrl(item)
            const imageUrl  = getImageUrl(item)
            const errors    = getErrors(item)
            const validating = isProcessing(item)
            const isDragTarget = dragOverIndex === i

            return (
              <div
                key={item.kind === "existing" ? `existing-${item.id}` : `new-${i}`}
                className={`media-preview${isDragTarget ? " media-preview--drag-over" : ""}`}
                draggable
                onDragStart={e => handleItemDragStart(e, i)}
                onDragOver={e => handleItemDragOver(e, i)}
                onDrop={e => handleItemDrop(e, i)}
                onDragEnd={handleItemDragEnd}
              >
                {videoUrl ? (
                  <>
                    <video
                      className="media-preview__img"
                      src={videoUrl}
                      preload="metadata"
                      muted
                      onClick={e => { e.stopPropagation(); setPlayUrl(videoUrl) }}
                      style={{ cursor: "pointer" }}
                    />
                    <div
                      className="media-preview__play-overlay"
                      onClick={e => { e.stopPropagation(); setPlayUrl(videoUrl) }}
                    >
                      <i className="bi bi-play-circle-fill" />
                    </div>
                  </>
                ) : imageUrl ? (
                  <img src={imageUrl} className="media-preview__img" alt={`media-${i}`} />
                ) : (
                  <div className="media-preview__video-icon"><i className="bi bi-film" /></div>
                )}

                {items.length > 1 && (
                  <div className="media-preview__order">{i + 1}</div>
                )}

                <button
                  className="media-preview__remove"
                  type="button"
                  onClick={e => { e.stopPropagation(); onRemoveFile(i) }}
                  aria-label="ファイルを削除"
                >×</button>

                {validating && (
                  <div className="media-preview__validating">
                    <span className="spinner" />
                  </div>
                )}
                {errors.length > 0 && <div className="media-preview__errors"><i className="bi bi-exclamation-circle" /></div>}
              </div>
            )
          })}
        </div>
      )}

      {allErrors.length > 0 && (
        <div className="media-errors">
          {allErrors.map(({ key, err }) => (
            <div key={key} className="media-errors__item">
              <i className="bi bi-exclamation-circle" />{err}
            </div>
          ))}
        </div>
      )}

      {tooManyFiles && (
        <div className="media-errors">
          <div className="media-errors__item">
            <i className="bi bi-exclamation-circle" />
            フィード・ストーリー・リール投稿はメディアを1枚にしてください（現在: {items.length}枚）。不要なファイルを削除するか、カルーセル投稿に変更してください。
          </div>
        </div>
      )}

      {postType === "carousel" && items.length > 0 && (items.length < 2 || items.length > 10) && !hasFileErrors && (
        <div className="media-errors">
          <div className="media-errors__item">
            カルーセル投稿は2〜10枚のメディアが必要です（現在: {items.length}枚）
          </div>
        </div>
      )}

      {carouselSizeError && (
        <div className="media-errors">
          <div className="media-errors__item">
            <i className="bi bi-exclamation-circle" />{carouselSizeError}
          </div>
        </div>
      )}

      {allWarnings.length > 0 && (
        <div className="media-warnings">
          {allWarnings.map(({ key, warn }) => (
            <div key={key} className="media-warnings__item">
              <i className="bi bi-exclamation-triangle" />{warn}
            </div>
          ))}
        </div>
      )}

      {/* 動画再生モーダル */}
      {playUrl && (
        <div className="media-play-modal" onClick={() => setPlayUrl(null)}>
          <div className="media-play-modal__inner" onClick={e => e.stopPropagation()}>
            <button className="media-play-modal__close" onClick={() => setPlayUrl(null)}>
              <i className="bi bi-x-lg" />
            </button>
            <video
              className="media-play-modal__video"
              src={playUrl}
              controls
              autoPlay
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default MediaUploader
